import React, { useState, useEffect, useCallback } from 'react';
import { getPracticeOverviewReviews, getPracticeOverviewReviewList } from 'api/group';
import {
    TPracticeOverviewReviewsVo,
    TPracticeOverviewReviewItemVo,
    TPracticeOverviewRange,
} from '../../types';
import RangeDropdown from '../RangeDropdown';
import s from './s.module.less';

const DEFAULT_RANGE: TPracticeOverviewRange = 'LAST_30D';

const StarIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
    <svg viewBox="0 0 20 20" fill={filled ? '#FFCB45' : '#F2F2F2'} width="18" height="18">
        <path d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.49L10 14.27 5.06 16.7 6 11.21l-4-3.9 5.53-.8L10 1.5z" />
    </svg>
);

const PatientInsights: React.FC = () => {
    const [range, setRange] = useState<TPracticeOverviewRange>(DEFAULT_RANGE);
    const [reviewsSummary, setReviewsSummary] = useState<TPracticeOverviewReviewsVo | null>(null);
    const [reviewList, setReviewList] = useState<TPracticeOverviewReviewItemVo[]>([]);
    const [activeTag, setActiveTag] = useState<string | null>(null);
    const [page, setPage] = useState(1);

    const fetchReviewList = useCallback((r: TPracticeOverviewRange, tag: string | null, pg: number, append = false) => {
        const params: { range: string; current: number; size: number; tag?: string } = { range: r, current: pg, size: 20 };
        if (tag) params.tag = tag;
        getPracticeOverviewReviewList(params).then((res) => {
            if (!res.error) {
                const list = res?.data?.data?.list ?? [];
                setReviewList((prev) => (append ? [...prev, ...list] : list));
            }
        });
    }, []);

    useEffect(() => {
        setPage(1);
        setActiveTag(null);
        getPracticeOverviewReviews({ range }).then((revRes) => {
            if (!revRes.error) setReviewsSummary(revRes?.data?.data ?? null);
        });
        fetchReviewList(range, null, 1);
    }, [range, fetchReviewList]);

    useEffect(() => {
        setPage(1);
        fetchReviewList(range, activeTag, 1);
    }, [activeTag, range, fetchReviewList]);

    const handleLoadMore = useCallback(() => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchReviewList(range, activeTag, nextPage, true);
    }, [range, activeTag, page, fetchReviewList]);

    if (!reviewsSummary) return null;

    const dist = reviewsSummary.distribution;
    const ratingDist = [dist.star5, dist.star4, dist.star3, dist.star2, dist.star1];
    const maxRating = Math.max(...ratingDist, 1);

    const positiveTags = (reviewsSummary.topTags ?? []).filter((t) => t.sentiment === 'POSITIVE');
    const negativeTags = (reviewsSummary.topTags ?? []).filter((t) => t.sentiment === 'NEGATIVE');

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    return (
        <div>
            <div className={s.header}>
                <h2 className={s.title}>Patient Insights</h2>
                <div className={s.headerRight}>
                    {(reviewsSummary.waitingReplyReviewCount > 0 || reviewsSummary.disputedReviewCount > 0) && (
                    <div className={s.badges}>
                        {reviewsSummary.waitingReplyReviewCount > 0 && (
                            <span className={s.waitingBadge}>
                                {reviewsSummary.waitingReplyReviewCount} waiting for reply
                            </span>
                        )}
                        {reviewsSummary.disputedReviewCount > 0 && (
                            <span className={s.disputedBadge}>
                                {reviewsSummary.disputedReviewCount} disputed
                            </span>
                        )}
                    </div>
                    )}
                    <RangeDropdown
                        value={range}
                        onChange={(v) => setRange(v as TPracticeOverviewRange)}
                    />
                </div>
            </div>
            <div className={s.card}>
                <div className={s.ratingSummary}>
                    <div className={s.ratingBig}>
                        <div className={s.ratingNum}>{reviewsSummary.avgRating?.toFixed(1) ?? '-'}</div>
                        <div className={s.stars}>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <StarIcon key={star} filled={star <= Math.round(reviewsSummary.avgRating ?? 0)} />
                            ))}
                        </div>
                        <div className={s.reviewCount}>{reviewsSummary.reviewCount} reviews</div>
                    </div>
                    <div className={s.ratingBars}>
                        {ratingDist.map((count, i) => (
                            <div key={i} className={s.barRow}>
                                <span className={s.barLabel}>{5 - i}</span>
                                <div className={s.barBg}>
                                    <div className={s.barFill} style={{ width: `${(count / maxRating) * 100}%` }} />
                                </div>
                                <span className={s.barCount}>{count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={s.insightsSection}>
                    {positiveTags.length > 0 && (
                        <>
                            <div className={s.insightsLabel}>Highlight</div>
                            <div className={s.pills}>
                                {positiveTags.map((t) => (
                                    <span
                                        key={t.tagName}
                                        className={`${s.pill} ${activeTag === t.tagName ? s.pillActive : ''}`}
                                        onClick={() => setActiveTag(activeTag === t.tagName ? null : t.tagName)}
                                    >
                                        {t.tagName} ({t.reviewCount})
                                    </span>
                                ))}
                            </div>
                        </>
                    )}
                    {negativeTags.length > 0 && (
                        <>
                            <div className={s.insightsLabel}>Common complaints</div>
                            <div className={s.pills}>
                                {negativeTags.map((t) => (
                                    <span
                                        key={t.tagName}
                                        className={`${s.pill} ${activeTag === t.tagName ? s.pillActive : ''}`}
                                        onClick={() => setActiveTag(activeTag === t.tagName ? null : t.tagName)}
                                    >
                                        {t.tagName} ({t.reviewCount})
                                    </span>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <div className={s.reviewList}>
                    {reviewList.map((review) => {
                        const tags = Array.isArray(review.tags) ? review.tags : [];
                        return (
                            <div key={review.reviewId} className={s.reviewItem}>
                                <div className={s.reviewContent}>
                                    <div className={s.reviewSubject}>
                                        {review.providerAvatarUrl ? (
                                            <img className={s.provAvatarImg} src={review.providerAvatarUrl} alt="" />
                                        ) : (
                                            <div className={s.provAvatar}>
                                                {(review.providerName ?? '')
                                                    .split(' ')
                                                    .map((n) => n[0])
                                                    .join('')
                                                    .slice(0, 2)}
                                            </div>
                                        )}
                                        <div>
                                            <div className={s.provName}>{review.providerName}</div>
                                            {review.providerCred && (
                                                <div className={s.provCred}>{review.providerCred}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className={s.reviewMeta}>
                                        <div className={s.reviewStars}>
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <StarIcon key={star} filled={star <= review.rating} />
                                            ))}
                                        </div>
                                        <span className={s.reviewDate}>{formatDate(review.submitAt)}</span>
                                        <span className={`${s.reviewBadge} ${review.isPublic ? s.badgePublic : s.badgePrivate}`}>
                                            {review.isPublic ? 'Public' : 'Private'}
                                        </span>
                                        <span className={s.reviewer}>· Verified patient</span>
                                    </div>
                                    {tags.length > 0 && (
                                        <div className={s.reviewTags}>
                                            {tags.map((tag, i) => (
                                                <span key={i} className={s.reviewTag}>{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                    {review.content && (
                                        <div className={s.reviewText}>{review.content}</div>
                                    )}
                                </div>
                                {review.isReplied && review.replyContent && (
                                    <div className={s.reviewResponse}>
                                        <span className={s.respFrom}>Response</span>
                                        {review.repliedAt && (
                                            <span className={s.respDate}>{formatDate(review.repliedAt)}</span>
                                        )}
                                        <span className={s.respLink}>View response</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {reviewList.length > 0 && reviewList.length % 20 === 0 && (
                    <div className={s.loadMore}>
                        <span className={s.loadMoreBtn} onClick={handleLoadMore}>
                            Load more reviews
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PatientInsights;
